import { createTheme } from '@mui/material/styles';

export const theme = createTheme({
  palette: {
    primary: { main: '#85D1DB', dark: '#6BB8C3', light: '#B3EBF2', contrastText: '#ffffff' },
    secondary: { main: '#B6F2D1', dark: '#A4E0BF', light: '#C9FDF2', contrastText: '#ffffff' },
    error: { main: '#ef4444' },
    warning: { main: '#f59e0b' },
    success: { main: '#10b981' },
    background: { default: '#f8fafc', paper: '#ffffff' },
    text: { primary: '#0f172a', secondary: '#64748b' },
  },
  typography: {
    fontFamily: "'Roboto', sans-serif",
    h1: { fontWeight: 700, letterSpacing: '-0.025em' },
    h2: { fontWeight: 700, letterSpacing: '-0.025em' },
    h3: { fontWeight: 700, letterSpacing: '-0.02em' },
    h4: { fontWeight: 600, letterSpacing: '-0.02em' },
    h5: { fontWeight: 600, letterSpacing: '-0.015em' },
    h6: { fontWeight: 600, letterSpacing: '-0.01em' },
    subtitle1: { fontWeight: 500 },
    subtitle2: { fontWeight: 600 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.57 },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.01em' },
  },
  shape: { borderRadius: 4 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontFamily: "'Roboto', sans-serif",
          fontWeight: 600,
          borderRadius: 6,
          padding: '10px 24px',
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:active': { transform: 'scale(0.98)' }
        },
        containedPrimary: {
          background: 'linear-gradient(to right, #60c2c0, #4eb1af)',
          color: '#ffffff',
          boxShadow: '0 4px 12px rgba(96, 194, 192, 0.3)',

          '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.9)',
            background: 'linear-gradient(to right, #60c2c0, #4eb1af)',
            opacity: 0.65,
            boxShadow: 'none',
          }
        },
        outlined: {
          borderWidth: '1.5px',
          borderColor: '#e2e8f0',
          color: '#475569',
          borderRadius: 6,
          boxShadow: '0 2px 6px rgba(0,0,0,0.02)',

        }
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: '1px solid rgba(226,232,240,0.8)',
          boxShadow: '0 4px 24px rgba(15,23,42,0.03), 0 1px 2px rgba(15,23,42,0.02)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          fontFamily: "'Roboto', sans-serif"
        },
        elevation1: {
          boxShadow: '0 4px 20px rgba(15,23,42,0.04), 0 1px 2px rgba(15,23,42,0.02)'
        }
      },
    },
    MuiTextField: {
  defaultProps: {
    variant: "outlined",
    size: "small",
  },
  styleOverrides: {
    root: {
      "& .MuiInputBase-root": {
        fontFamily: "'Roboto', sans-serif",
        fontSize: "0.9375rem",
        borderRadius: 6,
        backgroundColor: "#ffffff",
      },

"& .MuiInputLabel-root": {
        fontFamily: "'Roboto', sans-serif",
        color: "#64748b",
        whiteSpace: "nowrap",
        overflow: "visible",
        maxWidth: "none",
      },

      "& .MuiInputLabel-root.Mui-focused": {
        color: "#14b8a6",
      },

      "& .MuiInputLabel-root.MuiInputLabel-shrink": {
        backgroundColor: "#fff",
        padding: "0 4px",
        whiteSpace: "nowrap",
        overflow: "visible",
        maxWidth: "none",
      },

      "& .MuiOutlinedInput-notchedOutline legend": {
        whiteSpace: "nowrap",
      },
    },
  },
},
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          boxShadow: '0 1px 2px rgba(0,0,0,0.01)',
          '&.Mui-focused': {
            boxShadow: '0 0 0 4px rgba(133,209,219,0.15)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#85D1DB',
            borderWidth: '1.5px'
          }
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(15, 23, 42, 0.4)'
        },
        paper: {
          borderRadius: 12,
          boxShadow: '0 32px 64px rgba(15,23,42,0.15), 0 8px 16px rgba(15,23,42,0.1)',
          backgroundImage: 'linear-gradient(to bottom, #ffffff, #f8fafc)'
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '24px 32px 16px',
          '& .MuiTypography-root': {
            fontWeight: 700,
            fontSize: '1.25rem',
            letterSpacing: '-0.01em'
          }
        }
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '20px 32px 32px',
        }
      }
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 32px 24px',
          gap: '12px'
        }
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            backgroundColor: '#f8fafc',
            fontSize: '0.75rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: '#64748b',
            whiteSpace: 'nowrap',
            padding: '16px 24px',
            borderBottom: '1px solid #e2e8f0',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: "'Roboto', sans-serif",
          fontSize: '0.875rem',
          color: '#334155',
          borderBottom: '1px solid #f1f5f9',
          padding: '16px 24px',
          transition: 'background-color 0.2s',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.2s ease',

        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: "'Roboto', sans-serif",
          fontSize: '0.75rem',
          fontWeight: 600,
          borderRadius: 4,
          border: '1px solid rgba(0,0,0,0.05)',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: "'Roboto', sans-serif",
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.9375rem',
          minWidth: 'auto',
          padding: '12px 24px',
          transition: 'all 0.2s ease',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(15, 23, 42, 0.95)',
          color: '#ffffff',
          fontSize: '0.75rem',
          fontWeight: 600,
          padding: '8px 12px',
          borderRadius: 4,
          boxShadow: '0 8px 24px rgba(15,23,42,0.15)',
          backdropFilter: 'blur(4px)',
        },
        arrow: {
          color: 'rgba(15, 23, 42, 0.95)',
        },
      },
    },
    MuiSnackbar: {
      defaultProps: { anchorOrigin: { vertical: 'bottom', horizontal: 'right' } },
    },
  },
});

